import DefaultLayout from "../../../layouts/DefaultLayout";
import { Table } from "../../tables/BasicTable";
import React, { useContext, useEffect, useState } from "react";
import { useAuth } from "../../../providers/authProvider";
import BoxLayout from "../../../layouts/BoxLayout";
import DefaultButton from "../../buttons/DefaultButton";
import { ApiContext } from "../../contexts/ApiContext";
import { useNavigate, useParams } from "react-router-dom";
import H2Header from "../../headers/h2";
import TextInput from "../../input/TextInput";
import EditCompanyVisitModal from "../../modals/EditCompanyVisitModal";
import EditChildModal from "../../modals/EditChildModal";
import NewQuestionModal from "../../modals/NewQuestionModal";
import PresentSelectionModal from "../../modals/PresentSelectionModal";
import EditButton from "../../buttons/EditButton";
import DeleteButton from "../../buttons/DeleteButton";
import EditQuestionModal from "../../modals/EditQuestionModal";
import NewChildModal from "../../modals/NewChildModal";

// Reusable components
import SectionCard from "../../common/SectionCard";
import { InfoGrid, InfoItem } from "../../common/InfoGrid";
import LockedInfoBanner from "../../common/LockedInfoBanner";
import PresentServiceInfo from "../../common/PresentServiceInfo";
import QuestionsList from "../../common/QuestionsList";
import ConfirmButton from "../../common/ConfirmButton";

interface CompanyVisitDetails {
    ID: number;
    Company: string;
    Date: string;
    MinChildAge: number;
    MaxChildAge: number;
    StartTime: string;
    EndTime: string;
    NumberOfChildren: number;
    NumberOfAdults: number;
    MaxParticipants: number;
    SoftSignUpDeadline: string;
    HardSignUpDeadline: string;
    SignUpCode: string;
    Locked: boolean;
    Children: any[];
    Users: any[];
    PresentInfo: any[];
    Questions: any[];
    ChildQuestions: any[];
    PresentServiceActive: boolean;
    PresentCategory: string;
    Presents: any[];
}

function AdminCompanyVisitDetailPage() {
    const { Url } = useContext(ApiContext);
    const { token } = useAuth();
    const [companyVisitDetails, setCompanyVisitDetails] = useState<CompanyVisitDetails | any>(null);
    const [loading, setLoading] = useState(false);
    const [dataAvailable, setDataAvailable] = useState(false);
    const [openEditCompanyVisitModal, setOpenEditCompanyVisitModal] = useState(false);
    const [openAddChildModal, setOpenAddChildModal] = useState(false);
    const [openEditChildModal, setOpenEditChildModal] = useState(false);
    const [openPresentSelectionModal, setOpenPresentSelectionModal] = useState(false);
    const [openQuestionModal, setOpenQuestionModal] = useState(false);
    const [openEditQuestionModal, setOpenEditQuestionModal] = useState(false);
    const [childQuestion, setChildQuestion] = useState(false);
    const [rowDataPresent, setRowDataPresent] = useState([]);
    const [rowData, setRowData] = useState([]);
    const [Email, setEmail] = useState("");
    const [ParentEmail, setParentEmail] = useState("");
    const [message, setMessage] = useState("");
    const [currentQuestion, setCurrentQuestion] = useState("");
    const [tokenFetching, setTokenFetching] = useState(false);
    const [mailKey, setMailKey] = useState(0);
    const navigate = useNavigate();

    // Get ID from URL
    const params = useParams();

    const handleGenerateToken = (email: string) => {
        if (!email) return;
        setTokenFetching(true);
        fetch(Url + 'admin/generate-password-token/' + email, {
            method: 'GET',
            headers: {
                'content-type': 'application/json',
                'Authorization': "Bearer " + token,
            },
        })
            .then((res) => res.json())
            .then((data) => {
                if (data.token) {
                    setMessage('Nieuwe token gegenereerd voor ' + email);
                    getCompanyVisitDetails(); // Refresh all data to get updated tokens in lists
                    setTimeout(() => setMessage(''), 3000);
                }
            })
            .catch((err) => {
                console.error('Error generating token:', err);
                setMessage('Fout bij genereren token');
            })
            .finally(() => setTokenFetching(false));
    }

    const handleSend = async (emailData: any) => {
        setMessage('Bezig met versturen...');
        try {
            let finalBody = emailData.body;

            // If this is the "Factuur en Grote Boek" email (detected by subject)
            if (emailData.subject.toLowerCase().includes('factuur')) {
                setMessage('Token genereren...');
                const tokenRes = await fetch(Url + 'admin/generate-password-token/' + emailData.to, {
                    method: 'GET',
                    headers: {
                        'content-type': 'application/json',
                        'Authorization': "Bearer " + token,
                    },
                });
                const tokenData = await tokenRes.json();
                if (tokenData.token) {
                    // Try to replace common placeholders if they exist
                    // Since it's a contact person, we might not know their last token easily
                    // But we can check if 'ONBEKEND' is in the body or just generic [token] if it wasn't replaced
                    finalBody = finalBody.split('ONBEKEND').join(tokenData.token);
                    finalBody = finalBody.split('[token]').join(tokenData.token);
                }
            }

            setMessage('Bezig met versturen...');
            const response = await fetch(Url + 'admin/send-mail', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    sender: 'info@ridderendeprins.nl',
                    recipient: emailData.to,
                    cc: emailData.cc,
                    subject: emailData.subject,
                    htmlBody: finalBody
                })
            });

            if (response.ok) {
                setMessage('Email succesvol verzonden!');
                setTimeout(() => setMessage(''), 3000);
            } else {
                throw new Error('Failed to send email');
            }
        } catch (error) {
            console.error('Error sending email:', error);
            setMessage('Het versturen van de email is mislukt');
        }
    };

    const getColumnsChildern = () => [
        {
            Header: "ID",
            accessor: "ID",
        },
        {
            Header: "Voornaam",
            accessor: "FirstName",
        },
        {
            Header: "Achternaam",
            accessor: "LastName",
        },
        {
            Header: "Leeftijd",
            accessor: "Age",
        },
        {
            Header: "Email Ourder/verzorger",
            accessor: "Email",
        },
        {
            Header: "Naam Ouder/verzorger",
            accessor: "ParentName",
        },
        {
            Header: "Acties",
            id: "actions",
            Cell: ({ row }: { row: { original: any } }) => (
                <div className="flex gap-2">
                    <EditButton
                        onClick={() => handleRowData(row.original)}
                    />
                </div>
            )
        },
    ];

    const getColumnsContactPerson = () => [
        {
            Header: "ID",
            accessor: "ID",
        },
        {
            Header: "Voornaam",
            accessor: "FirstName",
        },
        {
            Header: "Achternaam",
            accessor: "LastName",
        },
        {
            Header: "Email",
            accessor: "Email",
        },
        {
            Header: "Acties",
            id: "actions",
            Cell: ({ row }: any) => (
                <div className="flex gap-2">
                    <DeleteButton
                        confirmText={`Weet je zeker dat je ${row.original.FirstName} ${row.original.LastName} als contactpersoon wilt verwijderen?`}
                        onConfirm={() => handleDeleteContactPerson(row.original)}
                    />
                </div>
            ),
        },
    ];

    const getColumnsPresents = () => [
        {
            Header: "Voornaam",
            accessor: "FirstName",
        },
        {
            Header: "Cadeau",
            accessor: "PresentName",
        },
        {
            Header: "Acties",
            id: "actions",
            Cell: ({ row }: { row: { original: any } }) => (
                <div className="flex gap-2">
                    <EditButton
                        onClick={() => handleRowDataPresents(row.original)}
                    />
                </div>
            )
        },
    ];

    const handleDownload = () => {
        fetch(Url + "admin/company-visit-export/" + params.id, {
            method: "GET",
            headers: {
                "content-type": "application/json",
                Authorization: "Bearer " + token,
            },
        })
            .then((response) => response.blob())
            .then((blob) => {
                const url = window.URL.createObjectURL(new Blob([blob]));
                const link = document.createElement("a");
                link.href = url;
                link.download = companyVisitDetails.Company + ".xlsx" || "downloaded-file";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            })
            .catch((error) => {
                console.error("Error fetching the file:", error);
            });
    };

    const handleAddQuestion = (isChild: boolean) => {
        setChildQuestion(isChild);
        setOpenQuestionModal(true);
    };
    const handleEditQuestion = (isChild: boolean) => {
        setChildQuestion(isChild);
        setOpenEditQuestionModal(true);
    };

    async function getCompanyVisitDetails() {
        if (!token) return;

        setLoading(true);
        try {
            const response = await fetch(`${Url}admin/company-visit-details/${params.id}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                setCompanyVisitDetails(data);
                setDataAvailable(true);
            } else {
                setDataAvailable(false);
                setCompanyVisitDetails([
                    {
                        message: "Geen informatie voor dit bedrijfsbezoek beschikbaar.",
                    },
                ]);
            }
        } catch (error) {
            console.error("Error fetching company visit details:", error);
            setDataAvailable(false);
        } finally {
            setLoading(false);
        }
    }

    function addContactPerson() {
        setMessage("Contactpersoon wordt gezocht in de database....");
        fetch(Url + "admin/company-visits-add-contact-person", {
            method: "PATCH",
            headers: {
                "content-type": "application/json",
                Authorization: "Bearer " + token,
            },
            body: JSON.stringify({
                ID: companyVisitDetails.ID,
                Email: Email,
            }),
        }).then((res) => {
            if (res.status === 200) {
                setMessage("Toevoegen van contactpersoon gelukt.");
                getCompanyVisitDetails();
            } else {
                setMessage(
                    "Contactpersoon niet gevonden in database, controleer het mailadres. Nieuwe gebruiker? Voeg dan eerst een gebruiker toe."
                );
            }
        });
    }

    function handleDeleteContactPerson(user: any) {
        if (!token || !user) return;

        setMessage("Contactpersoon wordt verwijderd...");
        fetch(Url + "admin/company-visits-remove-contact-person", {
            method: "DELETE",
            headers: {
                "content-type": "application/json",
                Authorization: "Bearer " + token,
            },
            body: JSON.stringify({
                ID: companyVisitDetails.ID,
                Email: user.Email,
            }),
        })
            .then((res) => {
                if (res.status === 200) {
                    setMessage("Contactpersoon verwijderd.");
                    getCompanyVisitDetails();
                } else {
                    setMessage("Verwijderen van contactpersoon is mislukt.");
                }
            })
            .catch(() => {
                setMessage("Er is een fout opgetreden bij het verwijderen.");
            });
    }

    useEffect(() => {
        setLoading(true);
        getCompanyVisitDetails();
    }, [token, Url]);

    useEffect(() => {
        getCompanyVisitDetails();
    }, [
        openEditCompanyVisitModal,
        openEditChildModal,
        openPresentSelectionModal,
        openQuestionModal,
        openEditQuestionModal,
        openAddChildModal,
    ]);

    function handleRowData(rowDataInput: any) {
        setRowData(rowDataInput);
        if (rowDataInput) {
            setOpenEditChildModal(true);
        }
    }

    function handleRowDataPresents(rowDataInput: any) {
        setRowDataPresent(rowDataInput);
        setOpenPresentSelectionModal(true);
    }

    function handleAddChildModel() {
        setOpenAddChildModal(true);
    }

    let dataTable;
    if (dataAvailable && companyVisitDetails?.Children) {
        dataTable = (
            <Table
                tableData={companyVisitDetails.Children}
                getColumns={getColumnsChildern()}
                storageKey={"VisitDetails"}
            ></Table>
        );
    } else {
        dataTable = <div>Er zijn nog geen kinderen ingeschreven voor dit bezoek</div>;
    }

    let contactPersonTable;
    if (dataAvailable && companyVisitDetails?.Users) {
        contactPersonTable = (
            <Table
                tableData={companyVisitDetails.Users}
                getColumns={getColumnsContactPerson()}
                storageKey={"VisitDetailsContactPersons"}
            ></Table>
        );
    } else {
        contactPersonTable = <div>Er zijn nog geen contactpersonen voor dit bezoek toegevoegd.</div>;
    }

    let childPresentTable;
    if (dataAvailable && companyVisitDetails?.PresentInfo) {
        childPresentTable = (
            <Table
                tableData={companyVisitDetails.PresentInfo}
                getColumns={getColumnsPresents()}
                storageKey={"VisitDetailsPresents"}
            ></Table>
        );
    } else {
        childPresentTable = <div>Er zijn nog geen kinderen ingeschreven voor dit bezoek</div>;
    }

    let questionsList
    if (dataAvailable && companyVisitDetails?.Questions){
        questionsList = (
            <QuestionsList
                questions={companyVisitDetails.Questions}
                onEdit={(q: any) => {
                    setCurrentQuestion(q);
                    handleEditQuestion(false);
                }}
                isChild={false}
            />
        );
    }

    let childQuestionsList;
    if (dataAvailable && companyVisitDetails?.ChildQuestions) {
        childQuestionsList = (
            <QuestionsList
                questions={companyVisitDetails.ChildQuestions}
                onEdit={(q: any) => {
                    handleEditQuestion(true);
                    setCurrentQuestion(q);
                }}
                isChild={true}
            />
        );
    }

    let presentServiceInfo;
    if (dataAvailable && companyVisitDetails) {
        presentServiceInfo = (
            <PresentServiceInfo
                active={!!companyVisitDetails.PresentServiceActive}
                category={companyVisitDetails.PresentCategory}
            />
        );
    }

    let lockedInfo;
    if (dataAvailable && companyVisitDetails) {
        lockedInfo = <LockedInfoBanner locked={!!companyVisitDetails.Locked} />;
    }

    let dataViewer;
    if (dataAvailable && companyVisitDetails) {
        let SignUpTarget;
        SignUpTarget = "/company/welcome?signup_code=" + companyVisitDetails.SignUpCode;
        dataViewer = (
            <BoxLayout>
                <div className="mb-4 mt-4">
                    <DefaultButton
                        content="Terug"
                        onClick={() => navigate("/admin/company-visits/", { replace: false })}
                    ></DefaultButton>
                </div>
                <H2Header headerText={companyVisitDetails.Company} />

                <SectionCard title="Algemeen">
                    {lockedInfo}
                    <InfoGrid>
                        <InfoItem label="Datum">{companyVisitDetails.Date}</InfoItem>
                        <InfoItem label="Min leeftijd kinderen">{companyVisitDetails.MinChildAge}</InfoItem>
                        <InfoItem label="Max leeftijd kinderen">{companyVisitDetails.MaxChildAge}</InfoItem>
                        <InfoItem label="Starttijd">{companyVisitDetails.StartTime}</InfoItem>
                        <InfoItem label="Eindtijd">{companyVisitDetails.EndTime}</InfoItem>
                        <InfoItem label="Aantal kinderen">{companyVisitDetails.NumberOfChildren}</InfoItem>
                        <InfoItem label="Aantal volwassen">{companyVisitDetails.NumberOfAdults}</InfoItem>
                        <InfoItem label="Totaal aantal inschrijvingen">{companyVisitDetails.NumberOfChildren + companyVisitDetails.NumberOfAdults}</InfoItem>
                        <InfoItem label="Zaalcapaciteit">{companyVisitDetails.MaxParticipants}</InfoItem>
                        <InfoItem label="Inschrijfdeadline (Zichtbaar)">{companyVisitDetails.SoftSignUpDeadline}</InfoItem>
                        <InfoItem label="Inschrijfdeadline (Systeem)" >{companyVisitDetails.HardSignUpDeadline}</InfoItem>
                        <InfoItem label="Aanmeldcode" >{companyVisitDetails.SignUpCode}</InfoItem>
                        <InfoItem label="Aanmeldlink" colspan={2}>
                            <a
                                className="text-blue-600 underline break-all"
                                href={SignUpTarget}
                                target="_blank"
                                rel="noreferrer"
                            >
                                {SignUpTarget}
                            </a>
                        </InfoItem>
                    </InfoGrid>
                    <div className="mb-4 mt-4">
                        <DefaultButton content="Bewerken"
                                       onClick={() => setOpenEditCompanyVisitModal(true)}></DefaultButton>

                    </div>
                    <div className="mb-4 mt-4">
                        <DefaultButton content="Exporteren (.xlsx)" onClick={handleDownload} />
                    </div>
                </SectionCard>

                <SectionCard title="Cadeauservice">{presentServiceInfo}</SectionCard>

                <SectionCard title="Contactpersonen">
                    {contactPersonTable}
                    <div className="mt-4 flex gap-2">
                        <TextInput
                            labelName="Email contactpersoon"
                            inputName="contactPersonEmail"
                            value={Email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Email"
                            type="email"
                        />
                        <div className="mt-6">
                            <DefaultButton content="Toevoegen" onClick={addContactPerson} />
                        </div>

                    </div>
                    {message && <div className="mt-2 text-sm text-gray-600">{message}</div>}
                </SectionCard>

                <SectionCard title="Kinderen">
                    <div className="mt-4 flex gap-2">
                        <TextInput
                            labelName="Email ouder"
                            inputName="contactPersonEmail"
                            value={ParentEmail}
                            onChange={(e) => setParentEmail(e.target.value)}
                            placeholder="Email"
                            type="email"
                        />
                        <div className="mb-4 mt-6 flex gap-2">
                            <DefaultButton content="Nieuw kind" onClick={handleAddChildModel} />

                        </div>
                    </div>
                    {dataTable}
                </SectionCard>

                <SectionCard title="Cadeaukeuzes">{childPresentTable}</SectionCard>

                <SectionCard title="Vragen (Volwassenen)">
                    <div className="mb-2">
                        <DefaultButton content="Nieuwe vraag" onClick={() => handleAddQuestion(false)} />
                    </div>
                    {questionsList}
                </SectionCard>

                <SectionCard title="Vragen (Kinderen)">
                    <div className="mb-2">
                        <DefaultButton
                            content="Nieuwe vraag"
                            onClick={() => handleAddQuestion(true)}
                        />
                    </div>
                    {childQuestionsList}
                </SectionCard>
            </BoxLayout>
        );
    }

    return (
        <DefaultLayout>
            {loading && !dataAvailable ? (
                <BoxLayout>
                    <div>Loading...</div>
                </BoxLayout>
            ) : (
                <div className="flex flex-col overflow-auto py-4 sm:py-0">
                    {dataAvailable ? (
                        dataViewer
                    ) : (
                        <BoxLayout>
                            <div>Geen informatie beschikbaar of je hebt geen rechten om deze informatie te bekijken.</div>
                        </BoxLayout>
                    )}
                </div>
            )}

            {/* Modals */}
            <EditCompanyVisitModal openModal={openEditCompanyVisitModal} setOpenModal={setOpenEditCompanyVisitModal} visitInfo={companyVisitDetails}/>
            <NewChildModal openModal={openAddChildModal} setOpenModal={setOpenAddChildModal}
                           welcomePage={false} signUpCode={companyVisitDetails?.SignUpCode} parentEmail={ParentEmail} />
            <EditChildModal openModal={openEditChildModal} setOpenModal={setOpenEditChildModal} welcomePage={false} rowData={rowData} admin={true}/>
            <NewQuestionModal openModal={openQuestionModal} setOpenModal={setOpenQuestionModal} CompanyVisitID={companyVisitDetails?.ID} ChildQuestion={childQuestion}/>
            <EditQuestionModal openModal={openEditQuestionModal} setOpenModal={setOpenEditQuestionModal} CompanyVisitID={companyVisitDetails?.ID} existingQuestion={currentQuestion} ChildQuestion={childQuestion}/>
            <PresentSelectionModal openModal={openPresentSelectionModal} setOpenModal={setOpenPresentSelectionModal} rowData={rowDataPresent} admin={true} presents={companyVisitDetails?.Presents} welcomePage={false}/>

        </DefaultLayout>
    );
}

export default AdminCompanyVisitDetailPage;